//////////////////
// IMPORTS
//////////////////
// libraries
import * as ISchema from "#webapp/src/__schema__";
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// relay
import { linksWelcomeFragments } from "./LinksWelcome.gql";

// styles
import { header, loadingBar, logoStyles, signupBtn, styles } from "./LinksWelcome.styles";

// material-ui
import { LinearProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

// state
import { loginUser } from "#webapp/src/mutations";
import { setOnboardingMeta } from "#webapp/src/state/actions/onboarding.actions";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";

//other components
import { LoginBtn } from "#webapp/src/components/login-btn";
import { LogoHorizontal } from "#webapp/src/components/logo";
import { M365WaitlistLink } from "#webapp/src/components/m365-waitlist-link";
import {
  OnboardingType,
  OnboardingTypeSource,
} from "#webapp/src/components/onboarding-nux/OnboardingNUXTypes";
import { H1 } from "#webapp/src/components/ui";
import { CookieDialog } from "@clockwise/web-commons/src/components/cookie-dialog";

//util
import { TrackingEvents, track } from "#webapp/src/util/analytics.util";
import { paths, url } from "@clockwise/client-commons/src/constants/site";
import { PageEvents, page } from "@clockwise/web-commons/src/util/analytics.util";
import Helmet from "react-helmet";

//////////////////
// TYPES
//////////////////

export interface ILinksWelcomeCmpt {
  viewer: ISchema.IViewer;
  redirect: string;
}

interface ILinksWelcomeProps extends ILinksWelcomeCmpt {
  setOnboardingMeta: typeof setOnboardingMeta;
}

const useStyles = makeStyles(styles);

//////////////////
// COMPONENT
//////////////////

const LinksWelcomeCmpt = (props: ILinksWelcomeProps) => {
  //state
  const [loading, setLoading] = React.useState(false);
  const [hideCookieErrorDialog, setHideCookieErrorDialog] = React.useState(true);

  //lifecycle
  const enableLinksOnboarding = () => {
    props.setOnboardingMeta(OnboardingType.Links, OnboardingTypeSource.LinksLandingPage);

    track(TrackingEvents.SEGMENTED_ONBOARDING.LINKS, {
      source: OnboardingTypeSource.LinksLandingPage,
    });
  };

  React.useEffect(() => {
    page(PageEvents.LINKS_ONBOARDING_LOGIN);
  }, []);

  const classes = useStyles();

  //callbacks
  const onClickConnectCalendar = () => {
    track(TrackingEvents.LINKS_ONBOARDING.LOGIN.CONNECT_CALENDER_CLICKED);
  };

  const onLoginStart = () => {
    track(TrackingEvents.AUTH.BEGIN_LOGIN);
    setLoading(true);
  };
  const onLoginSuccess = () => {
    track(TrackingEvents.AUTH.LOGIN);
    enableLinksOnboarding();
  };

  const onLoginFailure = () => {
    track(TrackingEvents.AUTH.LOGIN_FAILED);
    setLoading(false);
  };

  const onGoogleFailure = (e: any) => {
    if (e.error === "idpiframe_initialization_failed") {
      setHideCookieErrorDialog(false);
    }
  };

  const renderCookieDialog = () => {
    return <CookieDialog isChromeExtension={false} open={!hideCookieErrorDialog} />;
  };

  return (
    <div className={classes.containerFixed}>
      <Helmet>
        <title>Welcome to Clockwise · Clockwise</title>
      </Helmet>
      <div className={classes.popover}>
        <div className={classes.mainContainer}>
          {renderCookieDialog()}
          {loading && <p> loading</p>}
          <div className={classes.logoContainer}>
            <LogoHorizontal {...logoStyles} />
          </div>
          <div className={classes.content}>
            <H1 style={{ ...header, fontSize: 24 }}>Meet at the best time, every time!</H1>
            <div className={classes.lineItemContainer}>
              <div className={classes.lineItemBody}>
                Tell us about your meeting preferences and Clockwise will help you book the best
                time, taking into account your:
              </div>
            </div>
            <div className={classes.lineItemContainer}>
              <div className={classes.lineItemHeader}>
                {" "}
                <span>🕐</span> Preferred meeting hours
              </div>
              <div className={classes.lineItemHeader}>
                <span>🍛</span>Time for lunch
              </div>
              <div className={classes.lineItemHeader}>
                <span>💡</span>Focus Time{" "}
              </div>
              <div className={classes.lineItemHeader}>
                <span>🏠 </span>Personal commitments
              </div>
              <div className={classes.lineItemHeader}> and more!</div>
            </div>
          </div>
          <div className={classes.footer}>
            <LoginBtn
              label="Connect Your Google Calender"
              loginUserMutation={loginUser}
              style={signupBtn.base}
              labelStyle={signupBtn.label}
              onStart={onLoginStart}
              onSuccess={onLoginSuccess}
              onFailure={onLoginFailure}
              onGoogleFailure={onGoogleFailure}
              onClick={onClickConnectCalendar}
            />
            <M365WaitlistLink />
            <div className={classes.linksContainer}>
              <div>
                By connecting your calendar, you agree to Clockwise’s{" "}
                <a className={classes.footerLinks} href={`${url}${paths.terms}`}>
                  terms of service
                </a>{" "}
                and{" "}
                <a
                  className={classes.footerLinks}
                  href={`${url}${paths.privacy}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  privacy policy
                </a>
                .
              </div>
            </div>
          </div>
          {loading && <LinearProgress style={loadingBar} variant="indeterminate" />}
        </div>
      </div>
    </div>
  );
};

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: ILinksWelcomeCmpt) {
  return {};
}

const reduxContainer = connect(mapStateToProps, { setOnboardingMeta })(LinksWelcomeCmpt);

export const LinksWelcome = createFragmentContainer<ILinksWelcomeCmpt>(
  reduxContainer,
  linksWelcomeFragments,
);
